import { VIEWPORT_EVENTS } from '../../constants/Events';

export const VIEWPORT_TYPE = {
  DEFAULT: 'DEFAULT',
  NO_CROP: 'NO_CROP',
  CANVAS_BOUNDS: 'CANVAS_BOUNDS',
};

/**
 * @alias components.core.ViewportPlugin
 * @description
 * A viewport controller that calculates the players visible area of the game canvas.<br/>
 * Bounds are calculated in relation to game, and so you sould account for camera scroll if scrol factor is an issue when referencing
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.viewportController
 * </pre>
 *
 * The viewPortType can be configured within the [getConfig()]{@link getConfigBase} component options
 * <pre>
 * components : {
 *  viewportController : { data :  { viewPortType : VIEWPORT_TYPE } }
 * }
 * </pre>
 *
 * Or by calling the [setViewportType]{@link components.core.ViewportPlugin#setViewportType} method
 *
 * <pre>
 * this.game.appUrl.setViewportType( viewportType )
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', ViewportPlugin, true, 'scenekey' || null, { viewPortType : VIEWPORT_TYPE } );
 * </pre>
 *
 * @todo update the viewprort calculator to remove the need for a viewport type
 *
 */
class ViewportPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._viewPortType = VIEWPORT_TYPE.DEFAULT;
    this._viewport = { x: 0, y: 0, width: 0, height: 0, padding: 0, paddingBottom: 0 };
    this._events = new Phaser.Events.EventEmitter();
  }

  init(config) {
    config = config || {};
    this.viewPortType = config.viewPortType || VIEWPORT_TYPE.DEFAULT;
  }

  set viewPortType(viewportType) {
    this.setViewportType(viewportType);
  }

  get viewPortType() {
    return this._viewPortType;
  }

  setViewportType(viewportType) {
    this.resetViewport();
    this._viewPortType = viewportType || VIEWPORT_TYPE.DEFAULT;
    if (this._viewPortType === VIEWPORT_TYPE.DEFAULT) {
      this.game.scale.on('resize', this.updateViewport, this);
      this.updateViewport();
    } else {
      this._configureViewport(
        this.game.defaultDimensions.width,
        this.game.defaultDimensions.height
      );
    }
  }

  /**
   * The event emitter for this component.
   * @type {Phaser.Events.EventEmitter}
   * @readonly
   */
  get events() {
    return this._events;
  }

  /**
   * The games visible viewport
   * @type {ViewportArea}
   * @readonly
   */
  get viewport() {
    return { ...this._viewport };
  }

  /**
   * The viewport padding that should be applied when adding objects to the edge of the screen.<br>
   * By default is calculated at 2% of the longest dimension
   * @type {number}
   * @readonly
   */
  get padding() {
    return this.viewport.padding;
  }

  /**
   * The bottom viewport padding that should be applied when adding objects to the edge of the screen.<br>
   * The bottom of the viewport is given extra buffer on viewport devices due to Brim.js being used by clients
   * @type {number}
   * @readonly
   */
  get paddingBottom() {
    return this.viewport.paddingBottom;
  }

  /**
   * Get the visible width of the viewport
   * @type {number}
   * @readonly
   */
  get width() {
    return this.viewport.width;
  }

  /**
   * Get the visible height of the viewport
   * @type {number}
   * @readonly
   */
  get height() {
    return this.viewport.height;
  }

  /**
   * Get the x position of the viewport
   * @type {number}
   * @readonly
   */
  get x() {
    return this.viewport.x;
  }

  /**
   * Get the y position of the viewport
   * @type {number}
   * @readonly
   */
  get y() {
    return this.viewport.y;
  }

  /**
   * Get the center y position of the viewport
   * @type {number}
   * @readonly
   */
  get centerY() {
    return this.y + this.height * 0.5;
  }

  /**
   * Get the center x position of the viewport
   * @type {number}
   * @readonly
   */
  get centerX() {
    return this.x + this.width * 0.5;
  }

  /**
   * Get the center position of the viewport
   * @type {Object}
   * @readonly
   */
  get center() {
    return { x: this.centerX, y: this.centerY };
  }

  /**
   * Get the left position of the viewport
   * @type {number}
   * @readonly
   */
  get left() {
    return this.x;
  }

  /**
   * Get the right position of the viewport
   * @type {number}
   * @readonly
   */
  get right() {
    return this.x + this.width;
  }

  /**
   * Get the top position of the viewport
   * @type {number}
   * @readonly
   */
  get top() {
    return this.y;
  }

  /**
   * Get the bottom position of the viewport
   * @type {number}
   * @readonly
   */
  get bottom() {
    return this.y + this.height;
  }

  /**
   * Get the right position of the viewport, accounting for padding
   * @type {number}
   * @readonly
   */
  get rightPadded() {
    return this.right - this.padding;
  }

  /**
   * Get the left position of the viewport, accounting for padding
   * @type {number}
   * @readonly
   */
  get leftPadded() {
    return this.left + this.padding;
  }

  /**
   * Get the top position of the viewport, accounting for padding
   * @type {number}
   * @readonly
   */
  get topPadded() {
    return this.top + this.padding;
  }

  /**
   * Get the bottom position of the viewport, accounting for padding
   * @type {number}
   * @readonly
   */
  get bottomPadded() {
    return this.bottom - this.paddingBottom;
  }

  /**
   * Is orientation of viewport landscape
   * @type {bool}
   * @readonly
   */
  get isLandscape() {
    return this.viewport.landscape;
  }

  /**
   * Is orientation of viewport portrait
   * @type {bool}
   * @readonly
   */
  get isPortrait() {
    return !this.viewport.landscape;
  }

  /**
   * Update the viewport.
   * This is called automatically when the game window resizes.<br>
   * But you can also call it manually if for some reason it does not kick into action.
   */
  updateViewport() {
    if (this._viewPortType === VIEWPORT_TYPE.DEFAULT) {
      const windowAspect = this.game.scale.parentSize.aspectRatio;
      const height = this.game.defaultDimensions.height;
      const width = Math.min(
        this.game.defaultDimensions.width,
        this.game.defaultDimensions.height * windowAspect
      );
      this._configureViewport(width, height);
    }
  }

  /**
   * Reset the viewport to match the game, also removes the resize update
   */
  resetViewport() {
    this.game.scale.off('resize', this.updateViewport, this);
    this._configureViewport(this.game.defaultDimensions.width, this.game.defaultDimensions.height);
  }

  /** @private */
  _configureViewport(width, height) {
    const x = Math.max(0, this.game.centerPoint.x - width * 0.5);
    const y = Math.max(0, this.game.centerPoint.y - height * 0.5);

    const padding = Math.max(width * 0.02, height * 0.02);
    let paddingBottom = padding;

    // add a 32pixel botttom padding on iphone mobiles due to brim...
    if (window.innerWidth <= 568 && window.devicePixelRatio === 2) {
      paddingBottom = 32 / (this.game.canvas.clientHeight / height);
    }

    this._viewport.x = x;
    this._viewport.y = y;
    this._viewport.width = width;
    this._viewport.height = height;
    this._viewport.padding = padding;
    this._viewport.paddingBottom = paddingBottom;
    this._viewport.landscape = width >= height;

    this.events.emit(VIEWPORT_EVENTS.UPDATED, this._viewport);
  }

  /**
   * @private
   * @todo This should be moved into an extended spring roll version of the game
   */
  // Springroll scaling
  updateViewportCanvasBounds(opts) {
    if (opts) {
      const { x, y, width, height } = opts.viewArea;
      const landscape = width >= height;
      this._viewport = { x, y, width, height, padding: 0, paddingBottom: 0, landscape };
      this.events.emit(VIEWPORT_EVENTS.UPDATED, this._viewport);
    }
  }

  overlapsViewport(rect) {
    return checkRects(rect, this);
  }

  isWithinViewport(x, y) {
    return checkPoint(x, y, this);
  }

  destroy() {
    this.calculateViewport = null;
    this.game.scale.off('resize', this.updateViewport, this);
  }
}

export { ViewportPlugin };
